import { FC, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { ExploreGptsView } from './ExploreGptsView';
import { useState } from 'react';
import ShareGPT from '../ShareGPT/ShareGPT';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { useNotification } from '../../Contexts/NotificationContext';
import { useConfirm } from '../../Contexts/hooks';
import {
    ActionTypes,
    Conversation,
    ConversationCreate,
    IGptConfiguration,
    IUserPreferences,
    UserPreferences,
    GptStatus
} from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useConversations, useGptConfigurations, useUserPreferences, useFeatures } from '../../store/hooks';
import { setUserPreferencesAction } from '../../store/actions/userPreferencesAction';
import { addConversationAction } from '../../store/actions/conversationActions';
import { deleteGptConfiguration, editGptConfiguration, addGptConfiguration, unsubscribeGptConfiguration } from '../../store/actions/gptConfigurationsAction';
import { setConversationsAction } from '../../store/actions/conversationActions';
import { buildRoute, ChatRoute, PersonalGptSpecificRoute, ExploreGptRoute, ExploreGptSummaryRoute } from '../../Routes';
import { useUserContext } from '../../Contexts/UserContext';
import { useRootState } from '../../store/hooks';
import { loadGptConfigurations } from '../../Utils/gptConfigurationLoader';
import { getInstructionValidationErrorKey } from '../../Utils/personalGptErrors';

const ExploreGptsHandler: FC = () => {
    const [sharePopupOpen, setSharePopupOpen] = useState(false);
    const [shareGptId, setShareGptId] = useState<string | null>(null);
    const [deletingGptId, setDeletingGptId] = useState<string | null>(null);
    const navigate = useNavigate();
    const { dispatchNotification } = useNotification();
    const confirm = useConfirm();
    const { userPreferences, dispatch } = useUserPreferences();
    const { gptConfigurations, dispatch: gptDispatch } = useGptConfigurations();
    const { chats, dispatch: conversationDispatch } = useConversations();
    const { userProfile } = useUserContext();
    const { dispatch: rootDispatch, state } = useRootState();
    const { features } = useFeatures();

    // Reload GPT configurations when the page loads to get the latest data from the server
    useEffect(() => {
        performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                // Get the client configuration to use for attachment fallback
                const clientConfiguration = await client.getClientConfigurationsOptions(abortSignal);
                await loadGptConfigurations(client, rootDispatch, clientConfiguration, userProfile ?? null, abortSignal);
            } catch (e: unknown) {
                if ((e as Error).message !== 'canceled') {
                    dispatchNotification('error.api.get.gptConfigurations', 'error');
                }
            }
        });
    }, [rootDispatch, userProfile, dispatchNotification]);

    const onGptClick = useCallback(
        (gpt: IGptConfiguration) => {
            // Start a new chat with this GPT
            performEnterpriseGPTApiCall(async () => {
                try {
                    // If the GPT is default (SyGPT), check for existing draft conversation and navigate to it
                    if (gpt.isDefault) {
                        const draftConversation = chats.find((c) => c.id === 'DRAFT');
                        if (draftConversation) {
                            navigate(ChatRoute);
                            return;
                        }
                    }

                    // Create a draft conversation locally
                    const draftConversation = new Conversation({
                        id: 'DRAFT',
                        gpt: { id: gpt?.id, type: gpt?.type },
                        prompts: [],
                        title: '',
                        currentQuery: '',
                        startedOn: new Date(),
                        userName: userProfile?.username || '',
                        llm: '',
                    });

                    conversationDispatch(addConversationAction({ ...draftConversation, draft: true }));
                    navigate(ChatRoute);
                } catch (e) {
                    dispatchNotification('error.api.post.conversation', 'error');
                }
            });
        },
        [chats, conversationDispatch, dispatchNotification, navigate, userProfile],
    );

    const onGptEdit = useCallback(
        (gpt: IGptConfiguration) => {
            if (gpt.id) {
                navigate(buildRoute(PersonalGptSpecificRoute, { id: gpt.id }));
            }
        },
        [navigate],
    );

    const onGptTitleClick = useCallback(
        (gpt: IGptConfiguration) => {
            if (gpt.id) {
                // Navigate to the personal GPT edit page only for personal GPTs
                if (gpt.isPersonal) {
                    navigate(buildRoute(PersonalGptSpecificRoute, { id: gpt.id }));
                }
            }
        },
        [navigate],
    );

    const onGptShare = useCallback((gpt: IGptConfiguration) => {
        setShareGptId(gpt.id!);
        setSharePopupOpen(true);
    }, []);

    const handleShareClose = () => {
        setSharePopupOpen(false);
        setShareGptId(null);
    };

    const handleShare = (gptId: string, emails: string[], _message: string) => {
        if (!emails.length) return;

        performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                // First, get the latest version of the GPT from the server
                const currentGptData = await client.getPersonalGpt(gptId, abortSignal);

                // Get existing shared emails and append new ones (avoid duplicates)
                const existingEmails = currentGptData.sharedWith || [];
                const newEmails = emails.filter((email) => !existingEmails.includes(email));
                const updatedSharedWith = [...existingEmails, ...newEmails];

                // Update the GPT with the new shared list
                await client.modifyPersonalGpt(
                    gptId,
                    currentGptData.name,
                    currentGptData.description,
                    currentGptData.instruction,
                    [], // starters - preserve existing or empty
                    [], // filesToUpsert
                    [], // filesToDelete
                    updatedSharedWith, // sharedWith - this is what we're updating
                    currentGptData.isPublic || false, // isPublic - preserve existing
                    currentGptData.isDuplicable || false, // duplicable - preserve existing
                    abortSignal,
                );

                // Update the numberOfShares in local state
                const existingGpt = gptConfigurations.find((g) => g.id === gptId);
                if (existingGpt) {
                    const updatedGptEx = {
                        ...existingGpt,
                        numberOfShares: updatedSharedWith.length,
                    };
                    gptDispatch(editGptConfiguration(updatedGptEx));
                }

                dispatchNotification('GPT shared successfully!', 'success');
                // Don't close the dialog - user might want to share with more people
            } catch (error) {
                console.error('Error sharing GPT:', error);
                dispatchNotification(getInstructionValidationErrorKey('put', error) ?? 'Failed to share GPT. Please try again.', 'error');
            }
        });
    };

    const handleRemoveShare = async (gptId: string, emailToRemove: string): Promise<void> => {
        return performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                // First, get the latest version of the GPT from the server
                const currentGptData = await client.getPersonalGpt(gptId, abortSignal);

                // Remove the email from the shared list
                const updatedSharedWith = (currentGptData.sharedWith || []).filter((email) => email !== emailToRemove);

                // Update the GPT with the new shared list
                await client.modifyPersonalGpt(
                    gptId,
                    currentGptData.name,
                    currentGptData.description,
                    currentGptData.instruction,
                    [], // starters - preserve existing or empty
                    [], // filesToUpsert
                    [], // filesToDelete
                    updatedSharedWith, // sharedWith - this is what we're updating
                    currentGptData.isPublic || false, // isPublic - preserve existing
                    currentGptData.isDuplicable || false, // duplicable - preserve existing
                    abortSignal,
                );

                // Update the numberOfShares in local state
                const existingGpt = gptConfigurations.find((g) => g.id === gptId);
                if (existingGpt) {
                    const updatedGptEx = {
                        ...existingGpt,
                        numberOfShares: updatedSharedWith.length,
                    };
                    gptDispatch(editGptConfiguration(updatedGptEx));
                }

                dispatchNotification('User removed successfully!', 'success');
            } catch (error) {
                console.error('Error removing user from GPT:', error);
                dispatchNotification(getInstructionValidationErrorKey('put', error) ?? 'Failed to remove user. Please try again.', 'error');
            }
        });
    };

    const handleSetPublicStatus = async (gptId: string, isPublic: boolean): Promise<void> => {
        return performEnterpriseGPTApiCall(async (client, abortSignal) => {
            try {
                // First, get the latest version of the GPT from the server
                const currentGptData = await client.getPersonalGpt(gptId, abortSignal);

                // Update the GPT with the new isPublic status
                await client.modifyPersonalGpt(
                    gptId,
                    currentGptData.name,
                    currentGptData.description,
                    currentGptData.instruction,
                    [], // starters - preserve existing or empty
                    [], // filesToUpsert
                    [], // filesToDelete
                    currentGptData.sharedWith || [], // sharedWith - preserve existing
                    isPublic, // isPublic - set to the provided value
                    currentGptData.isDuplicable || false, // duplicable - preserve existing
                    abortSignal,
                );

                // Update the isPublic in local state
                const existingGpt = gptConfigurations.find((g) => g.id === gptId);
                if (existingGpt) {
                    const updatedGptEx = {
                        ...existingGpt,
                        isPublic,
                    };
                    gptDispatch(editGptConfiguration(updatedGptEx));
                }

                dispatchNotification(isPublic ? 'GPT is now public to the organization!' : 'GPT is now private!', 'success');
            } catch (error) {
                console.error('Error updating GPT public status:', error);
                dispatchNotification(
                    getInstructionValidationErrorKey('put', error) ?? `Failed to make GPT ${isPublic ? 'public' : 'private'}. Please try again.`,
                    'error',
                );
            }
        });
    };

    const onGptPin = useCallback(
        (gpt: IGptConfiguration) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    const isPinned = userPreferences.addedGpts?.includes(gpt.id!) ?? false;

                    let updatedUserPreferences: IUserPreferences;

                    if (isPinned) {
                        // Unpin the GPT
                        updatedUserPreferences = {
                            ...userPreferences,
                            addedGpts: userPreferences.addedGpts?.filter((x) => x !== gpt.id),
                        };
                    } else {
                        // Pin the GPT
                        updatedUserPreferences = {
                            ...userPreferences,
                            addedGpts: [...(userPreferences.addedGpts || []), gpt.id!],
                        };
                    }

                    await client.updateUserPreferences(new UserPreferences(updatedUserPreferences));
                    dispatch(setUserPreferencesAction(updatedUserPreferences));
                } catch (e) {
                    dispatchNotification('error.api.patch.userPreferences', 'error');
                }
            });
        },
        [userPreferences, dispatch, dispatchNotification],
    );



    const onGptUnsubscribe = useCallback(
        (gpt: IGptConfiguration) => {
            performEnterpriseGPTApiCall(async (client) => {
                try {
                    await client.unsubscribeSharedGpt(gpt.id!);
                    rootDispatch(unsubscribeGptConfiguration(gpt.id!));
                    dispatchNotification('personalGpt.actions.unsubscribe', 'success');
                } catch (e) {
                    dispatchNotification('error.api.post.personalGpt', 'error');
                }
            });
        },
        [rootDispatch, dispatchNotification],
    );

    const onGptDelete = useCallback(
        (gpt: IGptConfiguration) => {
            confirm({
                message: t('exploreGpt.confirmDeleteMessage'),
                show: true,
                title: t('exploreGpt.confirmDeleteTitle'),
                cancelText: t('No'),
                confirmText: t('Yes'),
            }).then(() => {
                setDeletingGptId(gpt.id!);
                performEnterpriseGPTApiCall(async (client, abortSignal) => {
                    try {
                        await client.deletePersonalGpt(gpt.id!);

                        // Remove conversations belonging to the deleted GPT from local store
                        const updatedConversations = chats.filter((c) => c.gpt?.id !== gpt.id);
                        rootDispatch(setConversationsAction(updatedConversations));

                        rootDispatch(deleteGptConfiguration(gpt.id!));
                        dispatchNotification('personalGpt.actions.delete', 'success');
                    } catch (e) {
                        dispatchNotification('error.api.delete.personalGpt', 'error');
                    } finally {
                        setDeletingGptId(null);
                    }
                });
            });
        }, [confirm, chats, rootDispatch, dispatchNotification]);

    const onGptDuplicate = useCallback((gpt: IGptConfiguration) => {
        performEnterpriseGPTApiCall(async (client) => {
            try {
                const newGpt = await client.duplicatePersonalGpt(gpt.id!);

                // Convert to GptConfigurationEx with isShared flag before adding to store
                const newGptEx = {
                    ...newGpt,
                    isShared: false,
                    numberOfChats: 0
                };

                // Update the list of gpts after duplication   
                rootDispatch(addGptConfiguration(newGptEx));
                dispatchNotification('personalGpt.actions.duplicate', 'success');
            } catch (e) {
                dispatchNotification('error.api.post.personalGpt', 'error');
            }
        });
    }, [rootDispatch, dispatchNotification, userProfile]);

    return (
        <>
            <ExploreGptsView
                gpts={gptConfigurations}
                onGptClick={onGptClick}
                onGptEdit={onGptEdit}
                onGptTitleClick={onGptTitleClick}
                onGptShare={onGptShare}
                onGptPin={onGptPin}
                onGptDuplicate={onGptDuplicate}
                onGptUnsubscribe={onGptUnsubscribe}
                onGptDelete={onGptDelete}
                deletingGptId={deletingGptId}
            />
            {sharePopupOpen && shareGptId && (
                <ShareGPT
                    open={sharePopupOpen}
                    gptId={shareGptId}
                    onClose={handleShareClose}
                    onShare={handleShare}
                    onRemove={handleRemoveShare}
                    onSetPublicStatus={handleSetPublicStatus}
                />
            )}
        </>
    );
};

export default ExploreGptsHandler;
